import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { blogPosts } from '../../mocks/blog';

const HomePage = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [visionRef, visionInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [rolesRef, rolesInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [worksRef, worksInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [blogRef, blogInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const featuredPosts = blogPosts.slice(0, 4);

  const roles = [
    {
      icon: 'ri-government-line',
      title: 'Fetih Partisi',
      subtitle: 'Genel Başkan',
      description: 'Türkiye\'nin güçlü, müreffeh ve bağımsız bir gelecek inşa etmesi için siyasi liderlik.'
    },
    {
      icon: 'ri-signal-tower-line',
      title: 'ULAK',
      subtitle: 'Genel Başkan',
      description: 'Yerli ve milli iletişim teknolojilerinin geliştirilmesi, Türkiye\'nin teknolojik bağımsızlığı.'
    },
    {
      icon: 'ri-briefcase-4-line',
      title: 'İş Dünyası',
      subtitle: 'Girişimci & İş İnsanı',
      description: 'Milli kalkınmaya katkı sağlayan yatırımlar ve girişimcilik faaliyetleri.'
    }
  ];

  return (
    <div className="min-h-screen bg-cream-light pb-16 lg:pb-0">
      <Header />

      {/* Hero Section */}
      <motion.section
        ref={heroRef}
        initial={{ opacity: 0 }}
        animate={heroInView ? { opacity: 1 } : {}}
        transition={{ duration: 0.8 }}
        className="relative h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-navy via-navy/95 to-navy/90"
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(212,175,55,0.15),transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(212,175,55,0.1),transparent_50%)]"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full pt-20 lg:pt-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={heroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span className="font-sans text-xs text-gold uppercase tracking-widest mb-3 block">
                Fetih Partisi Genel Başkanı · ULAK Genel Başkanı
              </span>
              <h1 className="font-serif text-5xl lg:text-7xl font-bold text-white tracking-wide mb-4">
                FATİH SİNAN YILMAZ
              </h1>
              <p className="font-sans text-xl lg:text-2xl text-gold italic mb-8">
                Millet için, Türkiye için, Gelecek için.
              </p>
              <p className="font-sans text-lg text-white/90 leading-relaxed mb-8">
                Siyasi liderlik, teknoloji ve iş dünyasındaki çalışmaları, vizyonu ve mücadelesiyle Fatih Sinan Yılmaz'ı yakından tanıyın.
              </p>
              <Link
                to="/hakkinda"
                className="inline-flex items-center bg-white text-navy font-sans font-semibold px-8 py-4 rounded-full hover:bg-gold hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Daha Fazla Keşfet
                <span className="ml-4 w-10 h-10 bg-navy text-white rounded-full flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={heroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex justify-center lg:justify-end"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gold/20 rounded-full blur-3xl"></div>
                <img
                  src="https://public.readdy.ai/ai/img_res/6d03d15e-6f53-464f-b0fa-814a6ff99640.png"
                  alt="Fatih Sinan Yılmaz"
                  className="relative w-full max-w-lg h-auto object-contain"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Roles Section */}
      <motion.section
        ref={rolesRef}
        initial={{ opacity: 0, y: 50 }}
        animate={rolesInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="bg-navy py-20 lg:py-28"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <span className="font-sans text-xs text-gold uppercase tracking-widest mb-3 block">Görevler & Sorumluluklar</span>
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-white">
              Üstlenilen Roller
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {roles.map((role, index) => (
              <motion.div
                key={role.title}
                initial={{ opacity: 0, y: 30 }}
                animate={rolesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-gold/10 rounded-xl mb-6">
                  <i className={`${role.icon} text-3xl text-gold`}></i>
                </div>
                <h3 className="font-serif text-2xl font-bold text-white mb-1">{role.title}</h3>
                <p className="font-sans text-sm text-gold mb-4 uppercase tracking-wider">{role.subtitle}</p>
                <p className="font-sans text-base text-gray-300 leading-relaxed">{role.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Vision Section */}
      <motion.section
        ref={visionRef}
        initial={{ opacity: 0, y: 50 }}
        animate={visionInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="bg-cream py-20 lg:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="font-sans text-sm text-gray-500 mb-4 block uppercase tracking-widest">/ Vizyon</span>
              <h2 className="font-serif text-4xl lg:text-5xl font-bold text-navy leading-tight mb-8">
                Güçlü Türkiye İçin <span className="text-gold">Kararlı Liderlik</span>
              </h2>
              <p className="font-sans text-lg text-gray-700 leading-relaxed mb-6">
                Fetih Partisi Genel Başkanı olarak Türkiye'nin her alanda güçlenmesi, milletin refahının artması ve ülkemizin uluslararası arenada hak ettiği yeri alması için kararlı adımlar atıyorum.
              </p>
              <p className="font-sans text-lg text-gray-700 leading-relaxed mb-8">
                ULAK Genel Başkanlığı göreviyle yerli ve milli iletişim teknolojilerinin geliştirilmesine öncülük ederek Türkiye'nin teknolojik bağımsızlığını pekiştiriyorum.
              </p>
              <Link
                to="/hakkinda"
                className="inline-flex items-center font-sans font-semibold text-navy hover:text-gold transition-colors duration-300 cursor-pointer"
              >
                Biyografiyi Oku
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>

            <div className="relative">
              <img
                src="https://static.readdy.ai/image/aaba9897d2facfbc7f1204630a4017ac/da07f78a96743b34a9fc5f0d74e31bb6.jpeg"
                alt="Fatih Sinan Yılmaz Vizyon"
                className="w-full h-auto rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </motion.section>

      {/* Featured Works Section */}
      <motion.section
        ref={worksRef}
        initial={{ opacity: 0, y: 50 }}
        animate={worksInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="py-20 lg:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-16">
            <h2 className="font-serif text-4xl lg:text-6xl font-bold text-navy leading-tight mb-4 lg:mb-0">
              Öne Çıkan<br />Çalışmalar
            </h2>
            <p className="font-sans text-lg text-gray-600 max-w-md">
              Siyaset, teknoloji ve iş dünyasında yürütülen projeler ve faaliyetler
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 bg-gray-100 rounded-3xl p-8 hover:shadow-xl transition-shadow duration-300">
              <div className="mb-6">
                <img
                  src="https://public.readdy.ai/ai/img_res/0e49cd19428916b974a84458e4eea0fc.jpg"
                  alt="Fetih Partisi"
                  className="w-full h-48 object-cover rounded-2xl"
                />
              </div>
              <span className="font-sans text-xs text-gold uppercase tracking-wider">Siyasi Liderlik</span>
              <h3 className="font-serif text-2xl font-bold text-navy mt-2 mb-3">
                Fetih Partisi Genel Başkanlığı
              </h3>
              <p className="font-sans text-base text-gray-600 leading-relaxed">
                Türkiye'nin güçlü ve müreffeh bir geleceği için siyasi mücadele ve liderlik
              </p>
            </div>

            <div className="bg-gray-100 rounded-3xl p-8 hover:shadow-xl transition-shadow duration-300">
              <span className="font-sans text-xs text-gold uppercase tracking-wider">Teknoloji Liderliği</span>
              <h3 className="font-serif text-2xl font-bold text-navy mt-2 mb-4">
                ULAK Genel Başkanlığı
              </h3>
              <div className="mb-4">
                <img
                  src="https://readdy.ai/api/search-image?query=advanced%20Turkish%20telecommunications%20technology%20center%2C%20modern%20communication%20infrastructure%2C%20high-tech%20facility%20with%20national%20branding%2C%20professional%20technology%20environment%2C%20innovation%20hub%2C%20clean%20modern%20interior%2C%20engineers%20working&width=600&height=400&seq=work2&orientation=landscape"
                  alt="ULAK"
                  className="w-full h-48 object-cover rounded-2xl"
                />
              </div>
              <p className="font-sans text-base text-gray-600 leading-relaxed">
                Yerli ve milli iletişim teknolojilerinin geliştirilmesi ve Türkiye'nin teknolojik bağımsızlığı
              </p>
            </div>

            <div className="bg-gray-100 rounded-3xl p-8 hover:shadow-xl transition-shadow duration-300">
              <span className="font-sans text-xs text-gold uppercase tracking-wider">İş Dünyası</span>
              <h3 className="font-serif text-2xl font-bold text-navy mt-2 mb-4">
                Girişimcilik & Yatırım
              </h3>
              <div className="mb-4">
                <img
                  src="https://readdy.ai/api/search-image?query=successful%20Turkish%20entrepreneur%20in%20modern%20Istanbul%20business%20district%2C%20corporate%20meeting%20with%20business%20partners%2C%20professional%20investment%20discussion%2C%20contemporary%20office%20environment%2C%20Bosphorus%20view%20in%20background%2C%20high%20quality%20business%20photography&width=600&height=400&seq=work3&orientation=landscape"
                  alt="İş Dünyası"
                  className="w-full h-48 object-cover rounded-2xl"
                />
              </div>
              <p className="font-sans text-base text-gray-600 leading-relaxed">
                Milli kalkınmaya katkı sağlayan yatırımlar ve iş dünyasındaki girişimcilik faaliyetleri
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Blog Preview Section */}
      <motion.section
        ref={blogRef}
        initial={{ opacity: 0, y: 50 }}
        animate={blogInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="bg-white py-20 lg:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-navy mb-4">
              Son Yazılar
            </h2>
            <p className="font-sans text-lg text-gray-600">
              Siyaset, teknoloji ve Türkiye'nin geleceği üzerine düşünceler
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {featuredPosts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                animate={blogInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={`/blog/${post.slug}`}
                  className="block bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={post.coverImage}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-7">
                    <span className="font-sans text-xs text-gold uppercase tracking-wider">
                      {post.category}
                    </span>
                    <h3 className="font-serif text-xl font-bold text-navy mt-2 mb-2 line-clamp-2 leading-snug">
                      {post.title}
                    </h3>
                    <p className="font-sans text-sm text-gray-500 mb-3">{post.date}</p>
                    <p className="font-sans text-base text-gray-600 line-clamp-3 leading-relaxed mb-4">
                      {post.excerpt}
                    </p>
                    <span className="font-sans text-sm font-medium text-gold group-hover:underline transition-colors duration-300">
                      Devamını Oku →
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/blog"
              className="inline-flex items-center font-sans font-semibold text-navy hover:text-gold transition-colors duration-300 cursor-pointer"
            >
              Tüm Yazıları Görüntüle
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </motion.section>

      <Footer />
    </div>
  );
};

export default HomePage;
